import React, { useState, useRef, useEffect, useCallback } from "react";

//Constantes
const MONTH_NAMES = [
    "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

const WEEK_DAYS = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];

//Helpers purs

const formatDate = (date) => {
    if (!date) return "";
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
};

const getDaysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();

const getFirstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

// Composant

const DateRangePicker = ({ onChangeDateRange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
    const [selectingStart, setSelectingStart] = useState(true);

    const calendarRef = useRef(null);

    //Click outside → fermer le calendrier
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (calendarRef.current && !calendarRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Handlers
    const toggleCalendar = useCallback(() => {
        setIsOpen((prev) => !prev);
    }, []);

    const clearDates = useCallback(() => {
        setStartDate(null);
        setEndDate(null);
        setIsOpen(false);
        setSelectingStart(true);
    }, []);

    const navigateMonth = useCallback((direction) => {
        setCurrentMonth((prevMonth) => {
            let newMonth = prevMonth + direction;
            if (newMonth > 11) {
                newMonth = 0;
                setCurrentYear((y) => y + 1);
            } else if (newMonth < 0) {
                newMonth = 11;
                setCurrentYear((y) => y - 1);
            }
            return newMonth;
        });
    }, []);

    const selectDate = useCallback((day) => {
        const selectedDate = new Date(currentYear, currentMonth, day);

        if (selectingStart || !startDate) {
            setStartDate(selectedDate);
            setEndDate(null);
            setSelectingStart(false);
            onChangeDateRange(selectedDate, null);
        } else {
            if (selectedDate < startDate) {
                // L'utilisateur a cliqué avant la date de début → on inverse
                setStartDate(selectedDate);
                setEndDate(startDate);
                setIsOpen(false);
                setSelectingStart(true);
                onChangeDateRange(selectedDate, startDate);
            } else {
                setEndDate(selectedDate);
                setIsOpen(false);
                setSelectingStart(true);
                onChangeDateRange(startDate, selectedDate);
            }
        }
    }, [currentYear, currentMonth, selectingStart, startDate, onChangeDateRange]);

    //Helpers de rendu
    const isDateSelected = useCallback((day) => {
        const date = new Date(currentYear, currentMonth, day);
        return (
            (startDate && date.getTime() === startDate.getTime()) ||
            (endDate && date.getTime() === endDate.getTime())
        );
    }, [currentYear, currentMonth, startDate, endDate]);

    const isDateInRange = useCallback((day) => {
        if (!startDate || !endDate) return false;
        const date = new Date(currentYear, currentMonth, day);
        return date >= startDate && date <= endDate;
    }, [currentYear, currentMonth, startDate, endDate]);

    const renderCalendar = () => {
        const daysInMonth = getDaysInMonth(currentMonth, currentYear);
        const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
        const days = [];

        // Jours du mois précédent (trailing)
        const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
        const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;
        const daysInPrevMonth = getDaysInMonth(prevMonth, prevYear);

        for (let i = firstDay - 1; i >= 0; i--) {
            days.push(
                <div key={`prev-${daysInPrevMonth - i}`} style={styles.dayInactive}>
                    {daysInPrevMonth - i}
                </div>
            );
        }

        // Jours du mois courant
        for (let day = 1; day <= daysInMonth; day++) {
            const selected = isDateSelected(day);
            const inRange = isDateInRange(day);

            days.push(
                <div
                    key={day}
                    style={{
                        ...styles.day,
                        ...(selected ? styles.daySelected : {}),
                        ...(inRange && !selected ? styles.dayInRange : {}),
                    }}
                    onClick={() => selectDate(day)}
                >
                    {day}
                </div>
            );
        }

        // Jours du mois suivant (leading)
        const totalCells = Math.ceil((firstDay + daysInMonth) / 7) * 7;
        const remainingCells = totalCells - (firstDay + daysInMonth);

        for (let day = 1; day <= remainingCells; day++) {
            days.push(
                <div key={`next-${day}`} style={styles.dayInactive}>
                    {day}
                </div>
            );
        }

        return days;
    };

    //Rendu
    const hasSelection = startDate && endDate;

    return (
        <div style={styles.container} ref={calendarRef}>
            <div style={styles.inputContainer} onClick={toggleCalendar}>
                <div style={styles.calendarIcon}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M2.91797 2.91667C2.5958 2.91667 2.33464 3.17783 2.33464 3.5V11.6667C2.33464 11.9888 2.5958 12.25 2.91797 12.25H11.0846C11.4068 12.25 11.668 11.9888 11.668 11.6667V3.5C11.668 3.17783 11.4068 2.91667 11.0846 2.91667H2.91797ZM1.16797 3.5C1.16797 2.5335 1.95147 1.75 2.91797 1.75H11.0846C12.0511 1.75 12.8346 2.5335 12.8346 3.5V11.6667C12.8346 12.6332 12.0511 13.4167 11.0846 13.4167H2.91797C1.95147 13.4167 1.16797 12.6332 1.16797 11.6667V3.5Z" fill="#6D7F92" />
                        <path fillRule="evenodd" clipRule="evenodd" d="M9.33333 0.582031C9.6555 0.582031 9.91667 0.843198 9.91667 1.16536V3.4987C9.91667 3.82086 9.6555 4.08203 9.33333 4.08203C9.01117 4.08203 8.75 3.82086 8.75 3.4987V1.16536C8.75 0.843198 9.01117 0.582031 9.33333 0.582031Z" fill="#6D7F92" />
                        <path fillRule="evenodd" clipRule="evenodd" d="M4.66536 0.582031C4.98753 0.582031 5.2487 0.843198 5.2487 1.16536V3.4987C5.2487 3.82086 4.98753 4.08203 4.66536 4.08203C4.3432 4.08203 4.08203 3.82086 4.08203 3.4987V1.16536C4.08203 0.843198 4.3432 0.582031 4.66536 0.582031Z" fill="#6D7F92" />
                        <path fillRule="evenodd" clipRule="evenodd" d="M1.16797 5.83333C1.16797 5.51117 1.42914 5.25 1.7513 5.25H12.2513C12.5735 5.25 12.8346 5.51117 12.8346 5.83333C12.8346 6.1555 12.5735 6.41667 12.2513 6.41667H1.7513C1.42914 6.41667 1.16797 6.1555 1.16797 5.83333Z" fill="#6D7F92" />
                    </svg>
                </div>

                {hasSelection ? (
                    <React.Fragment>
                        <div style={styles.datePill}>du {formatDate(startDate)}</div>
                        <div style={styles.datePill}>au {formatDate(endDate)}</div>
                    </React.Fragment>
                ) : (
                    <React.Fragment>
                        <div style={styles.placeholder}>Date de début...</div>
                        <div style={styles.placeholder}>Date de fin...</div>
                    </React.Fragment>
                )}

                {hasSelection && (
                    <div
                        style={styles.closeButton}
                        onClick={(e) => {
                            e.stopPropagation();
                            clearDates();
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path fillRule="evenodd" clipRule="evenodd" d="M15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8ZM5.46973 5.46967C5.76262 5.17678 6.2375 5.17678 6.53039 5.46967L8.00006 6.93934L9.4697 5.4697C9.76259 5.17681 10.2375 5.17681 10.5304 5.4697C10.8233 5.7626 10.8233 6.23747 10.5304 6.53036L9.06072 8L10.5304 9.46964C10.8233 9.76253 10.8233 10.2374 10.5304 10.5303C10.2375 10.8232 9.76259 10.8232 9.4697 10.5303L8.00006 9.06066L6.53039 10.5303C6.2375 10.8232 5.76262 10.8232 5.46973 10.5303C5.17684 10.2374 5.17684 9.76256 5.46973 9.46967L6.9394 8L5.46973 6.53033C5.17684 6.23744 5.17684 5.76256 5.46973 5.46967Z" fill="#6D7F92" />
                        </svg>
                    </div>
                )}
            </div>

            {isOpen && (
                <div style={styles.calendar}>
                    <div style={styles.calendarHeader}>
                        <button style={styles.navButton} onClick={() => navigateMonth(-1)}>
                            ‹
                        </button>
                        <div style={styles.monthYear}>
                            {MONTH_NAMES[currentMonth]} {currentYear}
                        </div>
                        <button style={styles.navButton} onClick={() => navigateMonth(1)}>
                            ›
                        </button>
                    </div>

                    <div style={styles.weekDays}>
                        {WEEK_DAYS.map((day) => (
                            <div key={day} style={styles.weekDay}>
                                {day}
                            </div>
                        ))}
                    </div>

                    <div style={styles.daysGrid}>{renderCalendar()}</div>
                </div>
            )}
        </div>
    );
};

const styles = {
    container: {
        position: "relative",
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontSize: "14px",
    },
    inputContainer: {
        display: "flex",
        alignItems: "center",
        backgroundColor: "#f0f0f0",
        borderRadius: "25px",
        padding: "0px 16px",
        cursor: "pointer",
        gap: "8px",
    },
    calendarIcon: {
        fontSize: "16px",
        color: "#666",
        paddingRight: "8px",
    },
    datePill: {
        backgroundColor: "#6b7280",
        color: "white",
        borderRadius: "20px",
        padding: "6px 16px",
        fontSize: "13px",
        fontWeight: "500",
    },
    placeholder: {
        color: "#999",
        padding: "6px 20px 6px 0px",
        width: "150px",
    },
    closeButton: {
        color: "white",
        borderRadius: "50%",
        width: "20px",
        height: "20px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "12px",
        cursor: "pointer",
        marginLeft: "auto",
    },
    calendar: {
        position: "absolute",
        top: "100%",
        left: "0",
        backgroundColor: "white",
        border: "1px solid #e5e7eb",
        borderRadius: "8px",
        boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
        padding: "16px",
        zIndex: 1000,
        minWidth: "280px",
        marginTop: "4px",
    },
    calendarHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "16px",
    },
    navButton: {
        background: "none",
        border: "none",
        fontSize: "18px",
        cursor: "pointer",
        padding: "4px 8px",
        borderRadius: "4px",
        color: "#374151",
    },
    monthYear: {
        fontSize: "16px",
        fontWeight: "600",
        color: "#111827",
    },
    weekDays: {
        display: "grid",
        gridTemplateColumns: "repeat(7, 1fr)",
        gap: "4px",
        marginBottom: "8px",
    },
    weekDay: {
        textAlign: "center",
        fontSize: "12px",
        color: "#6b7280",
        fontWeight: "500",
        padding: "8px 4px",
    },
    daysGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(7, 1fr)",
        gap: "2px",
    },
    day: {
        textAlign: "center",
        padding: "8px 4px",
        cursor: "pointer",
        borderRadius: "50%",
        fontSize: "14px",
        color: "#111827",
        minHeight: "32px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    daySelected: {
        backgroundColor: "#3b82f6",
        color: "white",
        fontWeight: "600",
    },
    dayInRange: {
        backgroundColor: "#dbeafe",
        color: "#1d4ed8",
    },
    dayInactive: {
        textAlign: "center",
        padding: "8px 4px",
        fontSize: "14px",
        color: "#d1d5db",
        minHeight: "32px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
};

export default DateRangePicker;


// WEBPACK FOOTER //
// ./app/components/common/DateRangePicker.js